"use client"

import Image from "next/image"
import Script from "next/script"
import SectionTitle from "../Common/SectionTitle"
import { useCallback, useEffect, useRef, useState } from "react"
import type { Locale } from "../../../i18n-config"
import axios from "axios"
import { toast } from "sonner"
import { getRuntimeEnv } from "@/libs/runtimeEnv"

const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA"

type TurnstileRenderOptions = {
  sitekey: string
  theme?: "light" | "dark" | "auto"
  callback?: (token: string) => void
  "expired-callback"?: () => void
  "error-callback"?: () => void
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: TurnstileRenderOptions
      ) => string
      reset: (widgetId?: string) => void
      remove: (widgetId: string) => void
    }
  }
}

function getTurnstileSiteKey() {
  const runtimeSiteKey =
    typeof window !== "undefined"
      ? getRuntimeEnv().NEXT_PUBLIC_TURNSTILE_SITE_KEY
      : undefined
  const buildSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

  return (
    runtimeSiteKey ||
    buildSiteKey ||
    (process.env.NODE_ENV === "production" ? "" : TURNSTILE_TEST_SITE_KEY)
  )
}

const Support = ({ lang, dictionary }: { lang: Locale; dictionary: any }) => {
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState<null | { ok: boolean; msg: string }>(
    null
  )
  const [siteKey, setSiteKey] = useState<string | null>(null)
  const [turnstileScriptLoaded, setTurnstileScriptLoaded] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState("")
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null)
  const turnstileWidgetIdRef = useRef<string | null>(null)
  const captchaConfigured = Boolean(siteKey)

  const resetTurnstile = useCallback(() => {
    setTurnstileToken("")

    if (turnstileWidgetIdRef.current) {
      window.turnstile?.reset(turnstileWidgetIdRef.current)
    }
  }, [])

  useEffect(() => {
    setSiteKey(getTurnstileSiteKey())
  }, [])

  useEffect(() => {
    if (
      !captchaConfigured ||
      !siteKey ||
      !turnstileScriptLoaded ||
      !turnstileContainerRef.current ||
      !window.turnstile ||
      turnstileWidgetIdRef.current
    ) {
      return
    }

    turnstileWidgetIdRef.current = window.turnstile.render(
      turnstileContainerRef.current,
      {
        sitekey: siteKey,
        theme: "dark",
        callback: token => {
          setTurnstileToken(token)
          setStatus(null)
        },
        "expired-callback": resetTurnstile,
        "error-callback": resetTurnstile,
      }
    )

    return () => {
      if (turnstileWidgetIdRef.current) {
        window.turnstile?.remove(turnstileWidgetIdRef.current)
        turnstileWidgetIdRef.current = null
      }
    }
  }, [captchaConfigured, resetTurnstile, siteKey, turnstileScriptLoaded])

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    if (!captchaConfigured || !turnstileToken) {
      setStatus({
        ok: false,
        msg:
          dictionary.page.home.contact["captcha_required"] ??
          "Please complete the verification before sending.",
      })
      return
    }

    setSubmitting(true)
    setStatus(null)

    const form = e.currentTarget
    const formData = new FormData(form)
    const payload = {
      name: String(formData.get("name") || "").trim(),
      email: String(formData.get("email") || "").trim(),
      message: String(formData.get("message") || "").trim(),
      turnstileToken,
    }

    const req = axios.post("/api/contact", payload)

    toast.promise(req, {
      loading:
        dictionary.page.home.contact["loading"] ?? "Sending your message...",
      success: () => (
        <b>
          {dictionary.page.home.contact["success"] ??
            dictionary.page.home.contact["success_message"] ??
            "Message sent!"}
        </b>
      ),
      error: err => (
        <b>
          {dictionary.page.home.contact["error"] ??
            err?.response?.data?.error ??
            dictionary.page.home.contact["error_message"] ??
            "Something went wrong."}
        </b>
      ),
    })

    req
      .then(() => {
        setStatus({
          ok: true,
          msg:
            dictionary.page.home.contact["success"] ??
            dictionary.page.home.contact["success_message"] ??
            "Message sent successfully.",
        })
        form.reset()
        resetTurnstile()
      })
      .catch(err => {
        setStatus({
          ok: false,
          msg:
            dictionary.page.home.contact["error"] ??
            err?.response?.data?.error ??
            dictionary.page.home.contact["error_message"] ??
            "Something went wrong.",
        })
        resetTurnstile()
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <section id="support" className="scroll-mt-17">
      {siteKey && (
        <Script
          src={TURNSTILE_SCRIPT_SRC}
          strategy="afterInteractive"
          onReady={() => setTurnstileScriptLoaded(true)}
        />
      )}
      <div className="max-w-[1104px] mx-auto px-4 sm:px-8 xl:px-0">
        <div className="relative z-999 overflow-hidden rounded-[30px] bg-dark pt-25 px-4 sm:px-20 lg:px-27.5">
          {/* grid background */}
          <div className="flex justify-center gap-7.5 absolute left-1/2 -translate-x-1/2 -top-[16%] max-w-[690px] w-full -z-1 opacity-40">
            <div className="max-w-[50px] w-full h-[250px] relative pricing-grid pricing-grid-border bottom-12" />
            <div className="max-w-[50px] w-full h-[250px] relative pricing-grid pricing-grid-border bottom-7" />
            <div className="max-w-[50px] w-full h-[250px] relative pricing-grid pricing-grid-border bottom-3" />
            <div className="max-w-[50px] w-full h-[250px] relative pricing-grid pricing-grid-border" />
            <div className="max-w-[50px] w-full h-[250px] relative pricing-grid pricing-grid-border" />
            <div className="max-w-[50px] w-full h-[250px] relative pricing-grid pricing-grid-border" />
            <div className="max-w-[50px] w-full h-[250px] relative pricing-grid pricing-grid-border bottom-2" />
            <div className="max-w-[50px] w-full h-[250px] relative pricing-grid pricing-grid-border bottom-5" />
            <div className="max-w-[50px] w-full h-[250px] relative pricing-grid pricing-grid-border bottom-8" />
          </div>

          {/* stars */}
          <div className="max-w-[482px] w-full h-60 overflow-hidden absolute -z-1 -top-30 left-1/2 -translate-x-1/2">
            <div className="stars" />
            <div className="stars2" />
          </div>

          {/* bg shapes */}
          <div className="absolute -z-10 pointer-events-none inset-0 overflow-hidden">
            <span className="absolute w-full h-full bg-[url(/images/blur/blur-19.svg)] bg-no-repeat bg-cover bg-center left-1/2 top-0 -translate-x-1/2 -z-1" />
            <span className="absolute left-1/2 top-0 -translate-x-1/2 -z-1 w-full aspect-[1170/592]">
              <Image
                src="/images/blur/blur-20.svg"
                alt="blur"
                fill
                className="max-w-none"
              />
            </span>
            <span className="absolute max-w-[530px] w-full mx-auto aspect-[530/254] left-1/2 top-0 -translate-x-1/2 -z-1">
              <Image
                src="/images/blur/blur-21.svg"
                alt="blur"
                fill
                className="max-w-none"
              />
            </span>
          </div>

          <SectionTitle
            subTitle={dictionary.page.home.contact["subTitle"]}
            title={dictionary.page.home.contact["title"]}
            paragraph={dictionary.page.home.contact["paragraph"]}
          />

          {/* support form */}
          <div className="form-box-gradient relative overflow-hidden rounded-[25px] bg-dark p-6 sm:p-8 xl:p-15">
            <form onSubmit={onSubmit} className="relative z-10">
              <div className="-mx-4 xl:-mx-10 flex flex-wrap">
                <div className="w-full px-4 xl:px-5 md:w-1/2">
                  <div className="mb-9.5">
                    <label
                      htmlFor="name"
                      className="text-white mb-2.5 block font-medium"
                    >
                      {dictionary.page.home.contact["name"]}
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      placeholder={
                        dictionary.page.home.contact["name_placeholder"]
                      }
                      required
                      className="rounded-lg border border-white/[0.12] bg-white/[0.05] focus:border-purple w-full py-3 px-6 outline-none text-white"
                    />
                  </div>
                </div>
                <div className="w-full px-4 xl:px-5 md:w-1/2">
                  <div className="mb-9.5">
                    <label
                      htmlFor="email"
                      className="text-white mb-2.5 block font-medium"
                    >
                      {dictionary.page.home.contact["email"]}
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      placeholder={
                        dictionary.page.home.contact["email_placeholder"]
                      }
                      required
                      className="rounded-lg border border-white/[0.12] bg-white/[0.05] focus:border-purple w-full py-3 px-6 outline-none text-white"
                    />
                  </div>
                </div>
                <div className="w-full px-4 xl:px-5">
                  <div className="mb-10">
                    <label
                      htmlFor="message"
                      className="text-white mb-2.5 block font-medium"
                    >
                      {dictionary.page.home.contact["message"]}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      placeholder={
                        dictionary.page.home.contact["message_placeholder"]
                      }
                      rows={6}
                      required
                      className="rounded-lg border border-white/[0.12] bg-white/[0.05] focus:border-purple w-full py-5 px-6 outline-none"
                    />
                  </div>
                </div>
                <div className="w-full px-4 xl:px-5">
                  <div className="mb-8 flex justify-center">
                    {siteKey === null ? null : captchaConfigured ? (
                      <div ref={turnstileContainerRef} />
                    ) : (
                      <p className="text-sm text-red-400" aria-live="polite">
                        {dictionary.page.home.contact[
                          "captcha_unavailable"
                        ] ??
                          "Verification is unavailable. Please try again later."}
                      </p>
                    )}
                  </div>
                  <div className="text-center">
                    <button
                      type="submit"
                      disabled={
                        submitting || !captchaConfigured || !turnstileToken
                      }
                      className="hero-button-gradient inline-flex rounded-lg py-3 px-7 text-white font-medium ease-in duration-300 hover:opacity-80 disabled:opacity-60"
                    >
                      {submitting
                        ? (dictionary.page.home.contact["sending"] ??
                          "Sending…")
                        : dictionary.page.home.contact["send_message"]}
                    </button>
                    {status && (
                      <p
                        className={`mt-3 text-sm ${status.ok ? "text-green-400" : "text-red-400"}`}
                        aria-live="polite"
                      >
                        {status.msg}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Support
